import { Info } from 'luxon'
import { FuzzyDate } from '@/rd/FuzzyDate'
import { HistoricalDateFormat } from '@/rd/HistoricalDateEnums'
import { MatchingCardPair, MatchingGameDetail } from './MatchingGameDefinition'

// dates are FuzzyDate protocol strings (e.g. "+1917-12-20"). A span starts with "S" and joins two with "/".
// Spans aren't presented yet, so we just take the start date.
function startDatePart(date: string) {
    return date.startsWith('S') ? date.slice(1).split('/')[0] : date
}

export function parseDate(date?: string) {
    if (!date) {
        return undefined
    }
    try {
        return FuzzyDate.parse(startDatePart(date))
    } catch {
        return undefined
    }
}

export function formatDate(date?: string) {
    return parseDate(date)?.toString(HistoricalDateFormat.ShortDisplay) ?? ''
}

// the year alone, for the emphasised part of an event date
export function dateYearPart(date?: string) {
    return parseDate(date)?.year
}

// the day + short month ("20 Dec", or just "Dec" when the day is unknown), shown small beneath the
// year; empty when the date has no month
export function dateDayMonthPart(date?: string) {
    const fd = parseDate(date)
    if (!fd?.month) {
        return ''
    }
    const month = Info.months('short')[fd.month - 1]
    return fd.day ? `${fd.day} ${month}` : month
}

export function detailByType(pair: MatchingCardPair, type: string) {
    return pair.details?.find(d => d.type === type)
}

export function birthYear(pair: MatchingCardPair) {
    return parseDate(detailByType(pair, 'Birth')?.date)?.year
}

export function deathYear(pair: MatchingCardPair) {
    return parseDate(detailByType(pair, 'Death')?.date)?.year
}

export function ageAt(pair: MatchingCardPair, date?: string) {
    const birth = birthYear(pair)
    const year = parseDate(date)?.year
    return birth !== undefined && year !== undefined ? year - birth : undefined
}

export function ordinalSuffix(n: number) {
    const suffixes = ['th', 'st', 'nd', 'rd']
    const v = n % 100
    return suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]
}

// label for an event row: for a marriage, "Marriage to <spouse first name>" -- the spouse is folded
// into value (the last " · " segment) in the new data model; otherwise a stated value, else the type
export function eventLabel(detail: MatchingGameDetail) {
    const spouse = detail.type === 'Marriage' ? (detail.spouse ?? detail.value?.split(' · ').pop()) : undefined
    if (spouse) {
        return `Marriage to ${spouse}`
    }
    return detail.value || detail.type
}
